import { Component, effect, inject, OnInit } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { KEYCLOAK_EVENT_SIGNAL, KeycloakEventType } from 'keycloak-angular';
import Keycloak from 'keycloak-js';
import { AuthService } from './core/auth/auth.service';
import { DebugLogger } from './shared/utils/debugger-console';

@Component({
  selector: 'app-root',
  template: '<router-outlet />',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [RouterOutlet],
})
export class AppComponent implements OnInit {
  private readonly _keycloak = inject(Keycloak);
  private readonly _keycloakSignal = inject(KEYCLOAK_EVENT_SIGNAL);
  private readonly _authService = inject(AuthService);
  private readonly _router = inject(Router);
  // Flag to prevent repeated token refresh attempts
  private _tokenRefreshAttempted = false;

  constructor() {
    effect(() => {
      const keycloakEvent = this._keycloakSignal();

      if (keycloakEvent.type === KeycloakEventType.TokenExpired) {
        this._keycloak.updateToken().catch(() => this._authService.signOut());
      }

      if (keycloakEvent.type === KeycloakEventType.AuthError) {
        this._authService.signOut();
      }
    });
  }

  ngOnInit(): void {
    if (!this._tokenRefreshAttempted) {
      const roles = this._authService.getUserRoles();
      this.waitRolWithAcademyAccess(roles).catch((err) => {
        DebugLogger.log('waitRolWithAcademyAccess failed', err);
      });
      this._tokenRefreshAttempted = true;
    }

    const path = window.location.pathname;
    if (path === '/') {
      this._router.navigateByUrl('/academy');
    } else {
      this._router.navigateByUrl(path);
    }
  }

  /**
   * Waits for the user to obtain at least one of the specified roles by repeatedly refreshing the authentication token
   * and checking the user's roles. The method attempts this process up to a maximum number of times, waiting a specified
   * interval between each attempt.
   *
   * The `-1` parameter in `updateToken(-1)` forces the token to be refreshed immediately, regardless of its expiration time.
   * This ensures that any recent changes to the user's roles are reflected as soon as possible.
   */
  private async waitRolWithAcademyAccess(
    rolesDesired: string[],
    maxAttempt = 5,
    ms = 800
  ): Promise<boolean> {
    for (let index = 0; index < maxAttempt; index++) {
      let tokenRefreshed = true;
      try {
        await this._keycloak.updateToken(-1);
      } catch {
        tokenRefreshed = false;
      }
      if (tokenRefreshed) {
        const roles = this._authService.getUserRoles();
        if (rolesDesired.some((role: string) => roles.includes(role))) {
          DebugLogger.log(`One of the desired roles found after ${index + 1} attempts.`);
          return true;
        }
      }
      await new Promise((resolve) => setTimeout(resolve, ms));
    }
    DebugLogger.log(`Role ${rolesDesired} not found after ${maxAttempt} attempts.`);
    return false;
  }
}
